import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { StyleShadowRoot } from "@redotech/react-util/style";
import { LoyaltyAutocheck } from "@redotech/redo-loyalty-common/loyalty-autocheck";
import { PopupContent } from "@redotech/redo-web/marketing/loyalty/popup/popup-content";
import { SlideInPanel } from "@redotech/redo-web/slide-in-panel";
import { ShopifyClientType } from "@redotech/shopify-client/ajax";
import { generateDiscountCode } from "@redotech/shopify-sdk/rpc/schema/loyalty/generate-discount-code";
import { invalidateCustomerDiscountCodes } from "@redotech/shopify-sdk/rpc/schema/loyalty/invalidate-customer-discount-codes";
import { isDef } from "@redotech/util/checks";
import { memo, useState } from "react";
import { RedoShopifyRpcClientContext } from "../../../client/redo-shopify-rpc-client";
import { ExtensionShopifyClientContext, isOnCartPage } from "../../../shopify";
import STYLE_MANAGER from "../../../style-manager";
import * as css from "./storefront.module.css";
import { useLoyaltyInfo } from "./use-loyalty-info";
import { useRenderEmbeddings } from "./use-render-embeddings";

export const LoyaltyStorefront = memo(function LoyaltyStorefront({
  loyaltySettings,
  storeUrl,
}: {
  loyaltySettings: LoyaltyAutocheck;
  storeUrl: string;
}) {
  const extensionShopifyClient = useRequiredContext(
    ExtensionShopifyClientContext,
  );
  const shopifyRpcClient = useRequiredContext(RedoShopifyRpcClientContext);

  const loyaltyInfoLoad = useLoyaltyInfo(storeUrl);
  const embeddingPortals = useRenderEmbeddings({ loyaltySettings });

  const isTapcart = extensionShopifyClient.type === ShopifyClientType.TAPCART;
  const [panelOpen, setPanelOpen] = useState(isOnCartPage(isTapcart));

  const closePanel = useHandler(() => {
    setPanelOpen(false);
  });

  const redeemPoints = useHandler(
    async (
      pointsToUse: number,
    ): Promise<{ discountCode: string; autoApplySuccessful: boolean }> => {
      const customerId = loyaltyInfoLoad.value?.loggedIn
        ? loyaltyInfoLoad.value.customerId
        : undefined;
      if (!customerId) {
        const msg = "No customer ID found";
        console.error(msg);
        throw new Error(msg);
      }

      const result = await shopifyRpcClient(generateDiscountCode, {
        selectedPointsToSpend: pointsToUse,
        shopifyCustomerId: customerId,
      });

      try {
        await extensionShopifyClient.applyDiscountCode?.(result.discountCode);
        return { discountCode: result.discountCode, autoApplySuccessful: true };
      } catch (error) {
        return {
          discountCode: result.discountCode,
          autoApplySuccessful: false,
        };
      }
    },
  );

  const undoRedeem = useHandler(async () => {
    try {
      await extensionShopifyClient.removeDiscountCodes?.();
    } catch (error) {
      console.error("Failed to remove discount code", error);
    }

    const customerId = loyaltyInfoLoad.value?.loggedIn
      ? loyaltyInfoLoad.value.customerId
      : undefined;
    if (!customerId) {
      console.error("No customer ID found");
      return;
    }

    await shopifyRpcClient(invalidateCustomerDiscountCodes, { customerId });
  });

  if (!loyaltySettings.enabled || !isDef(loyaltyInfoLoad.value)) {
    return null;
  }

  const loyaltyInfo = loyaltyInfoLoad.value;

  return (
    <>
      {/* Popup Mode */}
      {loyaltySettings.storefrontPopup.enabled && (
        <SlideInPanel
          anchorLocation={loyaltySettings.storefrontPopup.anchorPosition}
          handleCloseClick={closePanel}
          open={panelOpen}
        >
          <PopupContent
            className={css.popupContent}
            loyaltyInfo={loyaltyInfo}
            redeemPoints={redeemPoints}
            undoRedeem={undoRedeem}
          />
        </SlideInPanel>
      )}
      {/* Embedding Mode */}
      {embeddingPortals.map(({ foundSelector, portal }, index) => (
        <StyleShadowRoot
          key={index}
          shadowRoot={portal.root}
          styleManager={STYLE_MANAGER}
        >
          <PopupContent
            className={css.embeddingContent}
            loyaltyInfo={loyaltyInfo}
            redeemPoints={redeemPoints}
            undoRedeem={undoRedeem}
          />
        </StyleShadowRoot>
      ))}
    </>
  );
});
